import { isNullish } from '@sas/nova-core/typeguards';
import { PageControlDotSelected } from '@sas/react-svg';
import cx from 'classnames';
import { useBidiFormatter } from '../../../locale/useBidiFormatter';
import { postfixTestId } from '../../../util/testId';
import { Icon } from '../../Icon';
import { Flow } from '../../Layouts/Flow';
import { Link } from '../../Link';
import { Text } from '../../Text';
import './LinkItem.css';
import { renderDesignTokens, useDesignTokens } from '../../../theme';
import cn from './LinkItem.css.json';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const linkItemTestId = 'linkItem';
export const linkTestId = 'link';

/**
 * The LinkItem component that renders the Link element.
 *
 * <b>Note:</b> The LinkItem is meant to use either a ListProps'
 * `renderItem` or ListColumnProps' `item`, which will display
 * at the body of the item.
 *
 * @component
 * @param {LinkItemProps} props the LinkItemProps properties
 * @returns the LinkItem element
 */
export const LinkItem = props => {
  const {
    readOnly,
    onPress,
    testId,
    disabled,
    unread,
    description
  } = props;
  return /*#__PURE__*/_jsxs("div", {
    className: cx(cn.linkWrapper),
    onClick: e => {
      if (readOnly || disabled) return;
      const anchorElement = e.currentTarget.querySelector('a');
      const isAnchorElement = anchorElement && (anchorElement === e.target || anchorElement.contains(e.target));
      if (isAnchorElement) {
        onPress?.(e);
        e.stopPropagation();
      }
    },
    "data-testid": postfixTestId(testId, linkItemTestId),
    children: [unread === true ? /*#__PURE__*/_jsx(Icon, {
      icon: PageControlDotSelected,
      theme: () => {
        return {
          '--icon-default-override-fg': 'var(--list-item-unread-bg)'
        };
      },
      className: cn.dot,
      style: {
        pointerEvents: 'none'
      }
    }) : null, /*#__PURE__*/_jsxs(Flow, {
      spacing: "s",
      className: cx(cn.contentContainer, unread === true && cn.unread, unread === false && cn.read),
      children: [props.icon ? /*#__PURE__*/_jsx(ItemIcon, {
        ...props
      }) : null, description ? /*#__PURE__*/_jsx(DoubleLines, {
        ...props
      }) : /*#__PURE__*/_jsx(SingleLine, {
        ...props
      })]
    })]
  });
};
const SingleLine = props => {
  return /*#__PURE__*/_jsx("div", {
    className: cn.singleLine,
    children: /*#__PURE__*/_jsx(LinkContent, {
      ...props
    })
  });
};
const DoubleLines = props => {
  return /*#__PURE__*/_jsxs("div", {
    className: cn.doubleLines,
    children: [/*#__PURE__*/_jsx(LinkContent, {
      ...props
    }), /*#__PURE__*/_jsx(SubSection, {
      ...props
    })]
  });
};
const SubSection = props => {
  const {
    testId,
    disabled,
    unread,
    description
  } = props;
  return /*#__PURE__*/_jsx("span", {
    className: cn.subSection,
    children: /*#__PURE__*/_jsx(Text, {
      testId: postfixTestId(testId, 'description'),
      className: cx(cn.itemDescriptionText, unread === true && cn.unread, disabled && cn.disabled),
      disabled: disabled,
      wrapText: false,
      children: description ?? ''
    })
  });
};
const LinkContent = props => {
  const {
    unread,
    ...linkProps
  } = props;
  const {
    readOnly,
    onPress,
    testId,
    children,
    className,
    description,
    style,
    maxLines,
    disabled
  } = props;
  const dt = useDesignTokens();
  const tokens = getListItemDesignTokens(dt);
  const wrap = !isNullish(props.maxLines);
  const bidiFormatter = useBidiFormatter();
  return readOnly ? /*#__PURE__*/_jsx(Text, {
    className: cx(className, cn.link, description && cn.bold),
    style: style,
    wrapText: wrap,
    maxLines: maxLines,
    disabled: disabled,
    children: typeof children === 'string' ? bidiFormatter(children) : ''
  }) : /*#__PURE__*/_jsx(Link, {
    ...linkProps,
    onPress: onPress ? () => {} : undefined,
    wrapText: wrap,
    maxLines: props.maxLines,
    testId: postfixTestId(testId, linkTestId),
    className: cx(props.className, cn.link, props.description && cn.bold),
    style: {
      ...tokens,
      ...props.style
    }
  });
};
const ItemIcon = props => isNullish(props.icon) ? null : /*#__PURE__*/_jsx(Icon, {
  ...props.icon,
  className: cx(cn.itemIcon, props.icon.className),
  disabled: props.disabled
});
const getListItemDesignTokens = dt => {
  return {
    ...renderDesignTokens({
      'font-weight-thick': dt.tokens['font-style-weight-700']
    }, 'list-item')
  };
};
//# sourceMappingURL=LinkItem.js.map